import {useEffect, useState} from "react";
import {FieldErrors, useForm, useWatch} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Textarea} from "@/components/ui/textarea.tsx";
import {Switch} from "@/components/ui/switch.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import TextInputField from "@/components/forms/TextInputField.tsx";
import HiddenField from "@/components/forms/HiddenField.tsx";
import SelectInputField from "@/components/forms/SelectInputField.tsx";
import FormToolbar from "@/components/management/FormToolbar.tsx";
import {SaveState} from "@/components/buttons/SaveButton.tsx";
import MarkdownContent from "@/components/markdown/MarkdownContent.tsx";
import ImageUploadField from "@/images/ImageUploadField.tsx";
import {useTemporaryImageFormField} from "@/images/useTemporaryImageFormField.ts";
import {CharacterClasses} from "@/characters/model/CharacterClasses.ts";
import {SCHOOL_COLORS, SPELL_SCHOOLS} from "@/spells/model/SpellSchools.ts";
import {Spell} from "@/spells/model/Spell.ts";
import {SpellFormInput, SpellFormOutput, spellSchema, toSpellFormValues} from "@/spells/SpellSchema.ts";
import {notifyError} from "@/api/ErrorNotification.ts";

export default function SpellForm({spell, onSubmit, onSaved, onCancel, onDirtyChange}: {
    spell?: Spell;
    onSubmit: (spell: SpellFormOutput) => Promise<Spell>;
    onSaved: (spell: Spell) => void;
    onCancel: () => void;
    onDirtyChange: (dirty: boolean) => void;
}) {
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const [error, setError] = useState<string>();
    const form = useForm<SpellFormInput, unknown, SpellFormOutput>({
        resolver: zodResolver(spellSchema),
        mode: "onBlur",
        defaultValues: toSpellFormValues(spell),
    });
    const imageField = useTemporaryImageFormField({
        form,
        name: "image",
        onUploadStart: () => setError(undefined),
    });

    useEffect(() => onDirtyChange(form.formState.isDirty), [form.formState.isDirty, onDirtyChange]);

    const onValid = async (data: SpellFormOutput) => {
        setSaveState("saving");
        setError(undefined);
        try {
            const saved = await onSubmit(data);
            imageField.commit();
            form.reset(toSpellFormValues(saved));
            setSaveState("success");
            onSaved(saved);
        } catch (reason) {
            setSaveState("error");
            notifyError(reason, "Der Zauber konnte nicht gespeichert werden.");
        }
    };

    const onInvalid = (errors: FieldErrors<SpellFormOutput>) => {
        console.error("Form errors", errors);
        setSaveState("error");
        setError("Bitte überprüfe die Eingaben.");
    };

    const description = useWatch({control: form.control, name: "description"});

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onValid, onInvalid)} className="flex h-full min-h-0 flex-col gap-2">
                <HiddenField name="id"/>
                <FormToolbar title={spell ? "Zauber bearbeiten" : "Neuer Zauber"}
                             subtitle="Globaler Zauberkatalog"
                             saveState={saveState}
                             saveDisabled={!form.formState.isDirty || imageField.uploading || saveState === "saving"}
                             onCancel={onCancel}/>
                {error && <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
                <div className="min-h-0 flex-1 overflow-auto rounded-lg border bg-card">
                    <div className="grid gap-6 p-4 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]">
                        <div className="space-y-6">
                            <section className="grid gap-4 md:grid-cols-2">
                                <TextInputField name="name" title="Name" placeholder="Zaubername"/>
                                <TextInputField name="source" title="Quelle" placeholder="Spielerhandbuch"/>
                                <SelectInputField name="school" title="Schule"
                                                  options={Object.fromEntries(SPELL_SCHOOLS.map(school => [school, SCHOOL_COLORS[school].label]))}/>
                                <FormField control={form.control} name="level" render={({field}) => (
                                    <FormItem>
                                        <FormLabel>Grad</FormLabel>
                                        <FormControl>
                                            <Input type="number" min={0} max={9} inputMode="numeric" {...field}
                                                   onChange={event => field.onChange(event.target.valueAsNumber)}/>
                                        </FormControl>
                                        <FormMessage/>
                                    </FormItem>
                                )}/>
                            </section>

                            <FormField control={form.control} name="classes" render={({field}) => (
                                <FormItem>
                                    <FormLabel>Klassen</FormLabel>
                                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                                        {CharacterClasses.map(characterClass => (
                                            <label key={characterClass} className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/5">
                                                <Checkbox checked={field.value.includes(characterClass)}
                                                          onCheckedChange={checked => field.onChange(checked
                                                              ? [...field.value, characterClass]
                                                              : field.value.filter(value => value !== characterClass))}/>
                                                {characterClass}
                                            </label>
                                        ))}
                                    </div>
                                    <FormMessage/>
                                </FormItem>
                            )}/>

                            <section className="grid gap-4 md:grid-cols-2">
                                <TextInputField name="time" title="Zeitaufwand"/>
                                <TextInputField name="range" title="Reichweite"/>
                                <TextInputField name="components" title="Komponenten"/>
                                <TextInputField name="duration" title="Wirkungsdauer"/>
                                <TextInputField name="materialComponents" title="Materialkomponenten" className="md:col-span-2"/>
                            </section>

                            <section className="flex flex-wrap gap-6">
                                <BooleanField name="ritual" label="Ritual"/>
                                <BooleanField name="concentration" label="Konzentration"/>
                            </section>

                            <FormField control={form.control} name="description" render={({field}) => (
                                <FormItem>
                                    <FormLabel>Beschreibung (Markdown)</FormLabel>
                                    <FormControl>
                                        <Textarea {...field} className="min-h-64 resize-y font-mono text-sm"/>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}/>

                            <section>
                                <h2 className="mb-2 text-sm font-medium">Vorschau</h2>
                                <MarkdownContent markdown={description ?? ""}
                                                 className="min-h-24 rounded-md border bg-background p-3 text-sm"/>
                            </section>
                        </div>

                        <aside>
                            <h2 className="mb-2 text-sm font-medium">Bild</h2>
                            <ImageUploadField image={imageField.image}
                                              uploading={imageField.uploading}
                                              onFileSelected={imageField.onFileSelected}
                                              alt="Zauberbild"
                                              className="min-h-72 rounded-lg border"/>
                        </aside>
                    </div>
                </div>
            </form>
        </Form>
    );
}

function BooleanField({name, label}: {name: "ritual" | "concentration"; label: string}) {
    return (
        <FormField name={name} render={({field}) => (
            <FormItem className="flex items-center gap-3 rounded-md border px-3 py-2">
                <FormControl><Switch checked={field.value} onCheckedChange={field.onChange}/></FormControl>
                <FormLabel className="cursor-pointer">{label}</FormLabel>
            </FormItem>
        )}/>
    );
}
